import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Dynamic Routes & URL Parameters
//  Chapter 5 · Router
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_0 = `// In App.jsx routes
<Route path="/users/:userId"                 element={<UserProfile />} />
<Route path="/products/:category/:productId" element={<ProductDetail />} />
<Route path="/blog/:year/:month/:slug"       element={<BlogPost />} />`;

const CODE_1 = `import { useParams, useNavigate } from 'react-router-dom';

function ProductDetail() {
  const { category, productId } = useParams();  // From URL
  const navigate = useNavigate();
  const { data: product, loading, error } = useFetch(\`/api/products/\${productId}\`);

  if (loading) return <Spinner />;
  if (error)   return <NotFound />;

  return (
    <div>
      <button onClick={() => navigate(-1)}>← Back</button>
      <nav>
        <Link to="/products">All Products</Link> /
        <Link to={\`/products/\${category}\`}>{category}</Link> /
        <span>{product.name}</span>
      </nav>
      <h1>{product.name}</h1>
      <p>Category: {category}</p>
      <button onClick={() => navigate(\`/products/\${category}/\${productId}/edit\`)}>
        Edit
      </button>
    </div>
  );
}`;

const CODE_2 = `import { useSearchParams } from 'react-router-dom';

function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const search   = searchParams.get('search') || '';
  const category = searchParams.get('category') || 'all';
  const page     = Number(searchParams.get('page')) || 1;
  const sortBy   = searchParams.get('sort') || 'name';

  const updateFilter = (key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      if (key !== 'page') next.set('page', '1'); // Reset page on filter change
      return next;
    });
  };

  // URL becomes: /products?search=shoes&category=fashion&page=2&sort=price
  return (
    <div>
      <input
        value={search}
        onChange={e => updateFilter('search', e.target.value)}
        placeholder="Search products..."
      />
      <select value={category} onChange={e => updateFilter('category', e.target.value)}>
        <option value="all">All</option>
        <option value="fashion">Fashion</option>
        <option value="electronics">Electronics</option>
      </select>
      <button onClick={() => updateFilter('page', page + 1)}>Next Page →</button>
    </div>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function DynamicRoutesAndURLParams() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 5 · Router</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Dynamic Routes & URL Parameters</h1>
        <p style={styles.desc}>Build routes that accept variable segments and read URL parameters in components.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Route Parameters ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Route Parameters</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Route params are dynamic segments in a URL path defined with a colon prefix (:paramName). They let a single route match many different URLs." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_0, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_0)}</pre>
        </div>
      </div>

      /* ── Reading Params with useParams ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Reading Params with useParams</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "The useParams hook returns an object with all params from the current URL." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Query Parameters ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Query Parameters</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Query params (?key=value) don&#39;t need route definitions — read them with useSearchParams." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 2)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 2 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What is the difference between route params (:id) and query params (?id=1)?</div>
          <div style={styles.qItem}>✦ 2. How do you get all route params at once?</div>
          <div style={styles.qItem}>✦ 3. How do you programmatically navigate to a route with params?</div>
          <div style={styles.qItem}>✦ 4. How do you preserve existing query params when adding a new one?</div>
          <div style={styles.qItem}>✦ 5. Build a paginated list page that stores page, limit, and sort in the URL.</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
